/**
 * Agreement signing lives on the main site and its API on the main backend
 * (token checks, PDF, Stripe Checkout, email). Tokens are validated by the
 * backend on every request — nothing here grants access.
 */
import offerCatalog from './offerCatalog.json';
import { API_BASE } from '../config';

export const AGREEMENT_API_BASE = API_BASE.replace(/\/$/, '');

/** Absolute URL for an agreement API path such as `/api/agreement/<token>`. */
export function agreementApiUrl(path = '') {
  const clean = String(path || '');
  if (/^https?:\/\//i.test(clean)) return clean;
  return `${AGREEMENT_API_BASE}${clean.startsWith('/') ? '' : '/'}${clean}`;
}

/** Engagement kinds returned by the agreement API as `engagementKind`. */
export const ENGAGEMENT_KINDS = Object.freeze({
  GOAL_SETTING_MEETING: 'goal_setting_meeting',
  COMPREHENSIVE_PLAN: 'comprehensive_plan',
  ONGOING_PLANNING: 'ongoing_planning',
  COMPREHENSIVE_WITH_ONGOING: 'comprehensive_with_ongoing',
  PROJECT: 'project',
  HOURLY: 'hourly',
  SEMINAR: 'seminar',
});

const meeting = offerCatalog.goalSettingMeeting;
const usd = (amount) => `$${Number(amount).toLocaleString('en-US')}`;
const planPrice = usd(offerCatalog.offers.comprehensive_plan.price);
const ongoingPrice = usd(offerCatalog.offers.ongoing_planning.price);

/**
 * Plain-language context shown beside an agreement. This explains which
 * engagement the document covers; it does not restate or change its terms.
 */
const EXPLAINERS = {
  [ENGAGEMENT_KINDS.GOAL_SETTING_MEETING]: {
    label: `${meeting.name} agreement`,
    offerId: 'goal_setting_meeting',
    summary: `This agreement covers one ${meeting.durationMinutes}-minute ${meeting.name} at ${usd(meeting.price)}. It does not sign you up for a financial plan or a monthly service.`,
    points: [
      'We talk through your goals, your general questions, and the planning frameworks I use.',
      'Personalized financial and tax analysis is part of the comprehensive plan, not this meeting.',
      meeting.refundWording,
      meeting.creditWording,
    ],
  },
  [ENGAGEMENT_KINDS.COMPREHENSIVE_PLAN]: {
    label: 'Comprehensive financial plan agreement',
    offerId: 'comprehensive_plan',
    summary: `This agreement covers the comprehensive financial plan (${planPrice}). This is where the personalized financial and tax analysis happens.`,
    points: [
      'The fee shown below is the fee for your engagement. Check it against what we discussed before you sign.',
      `It does not start ongoing planning. That is a separate ${ongoingPrice}-per-month service with its own agreement.`,
    ],
  },
  [ENGAGEMENT_KINDS.ONGOING_PLANNING]: {
    label: 'Ongoing planning agreement',
    offerId: 'ongoing_planning',
    summary: `This agreement covers ongoing financial planning at the monthly fee shown below (${ongoingPrice} per month is the published rate).`,
    points: [
      'Ongoing planning is optional. It continues the work after a plan is in place.',
      'How to end the service and how fees are prorated are in the agreement itself. Read that section before you sign.',
    ],
  },
  [ENGAGEMENT_KINDS.COMPREHENSIVE_WITH_ONGOING]: {
    label: 'Comprehensive plan and ongoing planning agreement',
    offerId: 'comprehensive_plan',
    summary: 'This agreement covers both the comprehensive financial plan (the upfront fee) and ongoing planning (the monthly fee).',
    points: [
      'Both fees are shown below. Check them against what we discussed before you sign.',
      'If you only meant to start the plan, stop here and reply to my email. I will send the plan-only agreement.',
    ],
  },
  [ENGAGEMENT_KINDS.PROJECT]: {
    label: 'Project planning agreement',
    offerId: 'project_planning',
    summary: 'This agreement covers a fixed-fee project with the scope we agreed on.',
    points: ['The project fee and anything already paid are shown below.'],
  },
  [ENGAGEMENT_KINDS.HOURLY]: {
    label: 'Hourly planning agreement',
    offerId: 'hourly_planning',
    summary: 'This agreement covers planning time billed at the hourly rate shown below.',
    points: [],
  },
  [ENGAGEMENT_KINDS.SEMINAR]: {
    label: 'Seminar agreement',
    offerId: null,
    summary: 'This agreement covers a seminar or workshop at the fee shown below.',
    points: [],
  },
};

const FALLBACK_EXPLAINER = {
  label: 'Financial planning agreement',
  offerId: null,
  summary: 'This agreement covers the engagement described in the document below.',
  points: [],
};

export function getAgreementExplainer(engagementKind) {
  return EXPLAINERS[engagementKind] || FALLBACK_EXPLAINER;
}

/** Fallback when an older API response has no `engagementKind`. */
export function inferEngagementKind({ feeType, upfrontAmount, ongoingAmount, agreementVersion } = {}) {
  if (feeType === 'hourly') {
    return String(agreementVersion || '').startsWith('BPA-hourly-public')
      ? ENGAGEMENT_KINDS.GOAL_SETTING_MEETING
      : ENGAGEMENT_KINDS.HOURLY;
  }
  if (feeType === 'comprehensive') {
    const upfront = Number(upfrontAmount) || 0;
    const ongoing = Number(ongoingAmount) || 0;
    if (upfront > 0 && ongoing > 0) return ENGAGEMENT_KINDS.COMPREHENSIVE_WITH_ONGOING;
    if (ongoing > 0) return ENGAGEMENT_KINDS.ONGOING_PLANNING;
    return ENGAGEMENT_KINDS.COMPREHENSIVE_PLAN;
  }
  if (feeType === 'project') return ENGAGEMENT_KINDS.PROJECT;
  if (feeType === 'seminar') return ENGAGEMENT_KINDS.SEMINAR;
  return null;
}
